import { CompletedCasesDatedStats, TimeUnit } from "generated/graphql";
import { FC, useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectActiveFirmIdN } from "redux/slices/client";
import {
  fetchCompletedReviewsTimeline,
  selectAdminBackPeriod,
  selectCompletedReviewsTimeline,
  selectAdminTimeUnit,
} from "redux/slices/clientLens";
import theme from "theme";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { format } from "date-fns";
import formatHighchartsNumber from "helpers/formatHighchartsNumber";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";

interface CompletedReviewsTimelineProps {
  lensId: number;
  height: number;
}

const CompletedReviewsTimeline: FC<CompletedReviewsTimelineProps> = (props) => {
  const { lensId, height } = props;
  const snackpatch = useAppDispatchWithSnackbar();
  const completedReviewsTimeline = useAppSelector(selectCompletedReviewsTimeline);
  const unit = useAppSelector(selectAdminTimeUnit);
  const backPeriod = useAppSelector(selectAdminBackPeriod);
  const activeFirmId = useAppSelector(selectActiveFirmIdN);
  const [options, setOptions] = useState<Highcharts.Options>({
    chart: {
      type: "line",
      height,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: [],
      labels: {
        style: {
          fontFamily: "Inter,arial,sans-serif",
        },
      },
    },
    yAxis: {
      allowDecimals: false,
      title: {
        text: "",
      },
    },
    tooltip: {
      formatter() {
        return `${format(
          this.point.options.custom?.utcDate,
          this.point.options.custom?.unit === TimeUnit.Year ? "MMMM yyyy" : "MMMM d yyyy"
        )}<br>
                ${this.series.name} - <b>${formatHighchartsNumber(this.point.options.custom?.count)}</b>`;
      },
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          formatter() {
            return this.y ? formatHighchartsNumber(this.y) : 0;
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [],
  });

  useEffect(() => {
    if (completedReviewsTimeline) {
      setOptions({
        xAxis: {
          categories: completedReviewsTimeline.map((stat) =>
            format(stat.utcDate, unit === TimeUnit.Year ? "MMM yy" : "MMM d")
          ),
        },
        series: completedReviewsTimeline.reduce(
          (prev, stat) => {
            prev[0]?.data.push({
              y: stat.accumulatedCount,
              name: format(stat.utcDate, unit === TimeUnit.Year ? "MMMM yyyy" : "MMMM d yyyy"),
              options: {
                custom: {
                  count: stat.count,
                  utcDate: stat.utcDate,
                  unit,
                },
              },
            });
            return prev;
          },
          [
            {
              name: "Completed Reviews",
              type: "line",
              showInLegend: false,
              color: theme.palette.patternSecondary.main,
              data: [] as any[],
            },
          ]
        ),
      });
    }
  }, [completedReviewsTimeline]);

  useEffect(() => {
    void snackpatch(
      fetchCompletedReviewsTimeline({
        unit,
        backPeriod,
        lensId,
        firmId: activeFirmId,
      }),
      {
        errorMessage: "Failed to load completed reviews",
      }
    );
  }, [lensId, unit, backPeriod, activeFirmId]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default CompletedReviewsTimeline;

export function SBRCompletedReviewsTimeline(
  props: CompletedReviewsTimelineProps & { stats: CompletedCasesDatedStats[]; unit: TimeUnit }
) {
  const { height, stats, unit } = props;
  const [options, setOptions] = useState<Highcharts.Options>({
    chart: {
      type: "line",
      height,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: [],
      labels: {
        style: {
          fontFamily: "Inter,arial,sans-serif",
        },
        formatter: function () {
          return Highcharts.dateFormat("%b %e, %Y", new Date(this.value).getTime());
        },
      },
    },
    yAxis: {
      allowDecimals: false,
      title: {
        text: "",
      },
    },
    tooltip: {
      formatter() {
        return `${format(
          this.point.options.custom?.utcDate,
          this.point.options.custom?.unit === TimeUnit.Year ? "MMMM yyyy" : "MMM d, yyyy"
        )}<br>
                ${this.series.name} - <b>${formatHighchartsNumber(this.point.options.custom?.count)}</b>`;
      },
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          formatter() {
            return this.y ? formatHighchartsNumber(this.y) : 0;
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [],
  });

  useEffect(() => {
    setOptions({
      xAxis: {
        categories: stats.map((stat) => stat.datetime),
      },
      series: stats.reduce(
        (prev, stat) => {
          prev[0]?.data.push({
            y: stat.accumulatedCount,
            name: stat.datetime,
            options: {
              custom: {
                count: stat.count,
                utcDate: new Date(stat.datetime),
                unit,
              },
            },
          });
          return prev;
        },
        [
          {
            name: "Completed Reviews",
            type: "line",
            showInLegend: false,
            color: theme.palette.patternSecondary.main,
            data: [] as any[],
          },
        ]
      ),
    });
  }, [stats]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
